import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages the product size guide drawer.
 *
 * All case-size × wrist-size images are rendered up front by the Liquid block,
 * so selection is purely a client-side show/hide — no fetch, no re-render.
 *
 * @extends {DialogComponent}
 */
class SizeGuideComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    // Sync once on load so the visible image always matches the dataset, even
    // if Liquid and the dataset disagree about which combination is active.
    this.#render();
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Reads a data attribute off the clicked control.
   *
   * The value is taken from the DOM rather than passed through the `on:click`
   * URL, because the declarative binding runs values through `parseValue`,
   * which would turn "5.0" into the number 5 and stop it matching the
   * `data-wrist-option="5.0"` it needs to select.
   *
   * `event.target` is correct here, not `event.currentTarget`. The theme
   * delegates every `on:` binding from a single document listener, so
   * `currentTarget` is `document`; component.js proxies `target` to the element
   * carrying the attribute, so this stays right even if the button gains
   * children.
   *
   * @param {Event} event - The click event.
   * @param {string} key - The dataset key to read, e.g. "caseOption".
   * @returns {string | undefined} The attribute value, if present.
   */
  #optionValue(event, key) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return undefined;
    return target.dataset[key];
  }

  /**
   * Selects a watch case size.
   *
   * @param {Event} event - The click event.
   */
  selectCase = (event) => {
    const caseSize = this.#optionValue(event, 'caseOption');
    if (!caseSize) return;
    this.dataset.case = caseSize;
    this.#render();
  };

  /**
   * Selects a wrist size.
   *
   * @param {Event} event - The click event.
   */
  selectWrist = (event) => {
    const wristSize = this.#optionValue(event, 'wristOption');
    if (!wristSize) return;
    this.dataset.wrist = wristSize;
    this.#render();
  };

  /**
   * Switches the wrist measurement unit between centimetres and inches.
   *
   * @param {Event} event - The click event.
   */
  selectUnit = (event) => {
    const unit = this.#optionValue(event, 'unitOption');
    if (unit !== 'cm' && unit !== 'in') return;
    this.dataset.unit = unit;
    this.#render();
  };

  /**
   * Queries descendants, typed as elements that carry `dataset` and `hidden`.
   *
   * @param {string} selector - A CSS selector.
   * @returns {HTMLElement[]} The matching elements.
   */
  #all(selector) {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll(selector)]);
  }

  /**
   * Syncs pill pressed states, the visible image, and the unit labels to the
   * current dataset.
   */
  #render() {
    const { case: caseSize, unit } = this.dataset;
    const images = this.#all('[data-case-image]');

    // Wrist sizes are offered per case size, not globally — a merchant part-way
    // through uploading shouldn't see a pill with no image behind it.
    const forCase = images.filter((image) => image.dataset.caseImage === caseSize);
    const available = new Set(forCase.map((image) => image.dataset.wristImage));

    // If the selected wrist size isn't available for this case, move to one
    // that is.
    let wristSize = this.dataset.wrist;
    if (!available.has(wristSize)) {
      wristSize = forCase[0]?.dataset.wristImage;
      if (wristSize) this.dataset.wrist = wristSize;
    }

    for (const pill of this.#all('[data-case-option]')) {
      pill.setAttribute('aria-pressed', String(pill.dataset.caseOption === caseSize));
    }

    for (const pill of this.#all('[data-wrist-option]')) {
      const value = pill.dataset.wristOption;
      pill.hidden = !available.has(value);
      pill.setAttribute('aria-pressed', String(value === wristSize));
      const label = unit === 'in' ? pill.dataset.labelIn : pill.dataset.labelCm;
      if (label) pill.textContent = label;
    }

    for (const button of this.#all('[data-unit-option]')) {
      button.setAttribute('aria-pressed', String(button.dataset.unitOption === unit));
    }

    // Take the suffix from the active unit button so it stays translated.
    const activeUnit = this.#all('[data-unit-option]').find((button) => button.dataset.unitOption === unit);
    const suffix = this.querySelector('[data-unit-suffix]');
    if (suffix && activeUnit) suffix.textContent = `(${activeUnit.textContent?.trim() ?? ''})`;

    const active = forCase.find((image) => image.dataset.wristImage === wristSize) ?? forCase[0];

    for (const image of images) {
      image.hidden = image !== active;
    }
  }
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
